import clsx from "clsx";
import { GridLoader } from "react-spinners";
import { Program } from "../../../../core/models/db";
import { useIdentityStore, useSnackbarStore } from "../../../../core/store";
import { UploadFile } from "../../../components/drag-drop/model/UploadFile";
import { FileService } from "../../../components/drag-drop/services/file.service";
import { CContainer } from "@coreui/react-pro";
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoaderContext } from "../../../components/loader/context/LoaderContext";
import { useTranslation } from "react-i18next";
import { HiArrowNarrowLeft } from "react-icons/hi";
import { BASE_ROUTE, ROUTE_NAME_ID_2, RouteName } from "../../../../core/constants/route_name";
import { useProgram, useSEO } from "../../../../core/hooks/db/fisioteduca/useFisioteduca";
import * as MessageSnackbarDefault from "../../../../core/constants/message-snackbar/msgFormDefault";
import { Util } from "../../../../core/util/util";
import ProgramForm from "./ProgramForm";
import moment from "moment";

const ProgramDetail = () => {
    const { t } = useTranslation();
    const { identity } = useIdentityStore((state) => state);
    const navigate = useNavigate();

    const [program, setProgram] = useState<Program>(undefined);
    const [loading, setLoading] = useState(false);
    const [formHeight, setFormHeight] = useState(0);
    const formRef = useRef(null);
    const [typeAction, setTypeAction] = useState<"add" | "update">(null);

    const { handleLoading } = useContext(LoaderContext);
    const { addSnackbar, setMaxSnackbars } = useSnackbarStore();

    const { getProgramById, addProgram: add, updateProgram: update } = useProgram();
    const { checkSpecificParam: checkSpecificParamSEO } = useSEO(); // Si tienes un método para SEO

    const { id } = useParams();

    useEffect(() => {
        if (formRef.current) {
            setFormHeight(formRef.current.clientHeight);
        }
    }, [loading, program]);

    /**
     * Cargar Program al iniciar
     */
    useEffect(() => {
        (async () => {
            setLoading(true);


            if (id) {
                const result = await getProgramById(id);
                if (result && result.ok && result.item) {
                    setProgram(result.item);
                    setTypeAction("update");
                } else {
                    console.warn("No se encontró ningún id de Program");
                    const pagesRoutes = t("routes", { returnObjects: true });
                    const newRoute = ROUTE_NAME_ID_2({}, pagesRoutes, RouteName.program);
                    navigate(`${BASE_ROUTE}${newRoute}`);
                }
            } else {
                // Crear nuevo
                let program = new Program();
                setProgram(program);
                setTypeAction("add");
            }
            setLoading(false);
        })();
    }, []);

    /**
     * Creación de archivo (subida)
     */
    const createFile = async (file: any, id: string) => {
        try {
            const { data } = await FileService.add(new UploadFile(file, ["images", "program", id]), null);
            if (data.ok) {
                const pathRelative = `/${data.item[0].destination}/${data.item[0].filename}`;
                return pathRelative;
            } else {
                return undefined;
            }
        } catch (e) {
            throw e;
        }
    };

    // Función auxiliar para convertir un blob a archivo y subirlo
    const convertBlob = async (
        blobStr: string,
        programId: string,
        prefix: string
    ): Promise<string> => {
        try {
            // Puedes ajustar la detección de extensión según lo necesites
            const extension = ".png";
            const fileName = `${prefix}_${Date.now()}${extension}`;
            const file = await Util.transformBlobToFile(blobStr, fileName);
            const { data } = await FileService.add(
                new UploadFile(file, ["images", "program", programId]),
                null
            );
            if (data.ok && data.item?.[0]) {
                return `/${data.item[0].destination}/${data.item[0].filename}`;
            }
        } catch (error) {
            console.error("Error converting blob:", error);
        }
        // Si ocurre algún error, se retorna el blob original
        return blobStr;
    };

    // Función principal para convertir todos los blobs dentro del JSON de contenido
    const convertAllBlobsFromJson = async (
        jsonString: string,
        programId: string
    ): Promise<string> => {
        try {
            // Parseamos el JSON
            const contentArray = JSON.parse(jsonString);

            // Iteramos sobre cada bloque de contenido
            for (const block of contentArray) {
                // Procesamos el campo "image"
                if (block.image && typeof block.image === "string" && block.image.startsWith("blob:")) {
                    // Usamos un timestamp único para este campo
                    block.image = await convertBlob(block.image, programId, `program_image_${Date.now()}`);
                }
                // Procesamos el campo "listImage"
                if (block.listImage && typeof block.listImage === "string" && block.listImage.startsWith("blob:")) {
                    block.listImage = await convertBlob(block.listImage, programId, `program_listImage_${Date.now()}`);
                }
                // Procesamos el array "imageTextItems"
                if (block.imageTextItems && Array.isArray(block.imageTextItems)) {
                    await Promise.all(
                        block.imageTextItems.map(async (item, i) => {
                            if (item.image && typeof item.image === "string" && item.image.startsWith("blob:")) {
                                let randomNumber = Math.floor(Math.random() * 100000 + 1);

                                // Cada imagen obtiene su propio timestamp
                                item.image = await convertBlob(item.image, programId, `program_imageText_${i}_${randomNumber}`);
                            }
                        })
                    );
                }
                // Si en el futuro se agregan otros campos que puedan contener blobs, agrégalos aquí.
            }


            return JSON.stringify(contentArray);
        } catch (error) {
            console.error("Error al convertir blobs en JSON:", error);
            return jsonString;
        }
    };

    /**
     * Update
     */
    const updateProgram = useCallback(async (
        program: Program,
        imageItem: {
            oldImage: string;
            image: string;
            nameImage: string;
        }
    ) => {
        try {
            setLoading(true);
            
            console.log("editando Program con ID:", id);
            console.log("editando Program con ID:", id);
            console.log("editando Program con ID:", id);

            const prCopy = { ...program };
            const { oldImage, image, nameImage } = imageItem;

            // Manejo de imagen en blob
            if (image && image.startsWith("blob")) {
                const lastDotIndex = nameImage.lastIndexOf(".");
                // const name = lastDotIndex > -1 ? nameImage.slice(0, lastDotIndex) : nameImage;
                const name = Date.now() + "_" + Math.random().toString(36).substring(7);
                const ext = lastDotIndex > -1 ? nameImage.slice(lastDotIndex) : ".png";
                const formattedName = name.replace(/ /g, "_");
                let file = await Util.transformBlobToFile(
                    image,
                    formattedName + "_" + Date.now() + ext
                );
                prCopy.image = await createFile(file, prCopy.id);
            }

            // Si la imagen se ha subido correctamente, eliminar la anterior (si procede)
            if (oldImage && oldImage !== prCopy.image) {
                await FileService.delete(oldImage);
            }

            if (!prCopy.startPublicationDate) {
                prCopy.startPublicationDate = moment().subtract(5, "minutes").toDate();
            }

            if (prCopy?.jsonContent) {
                console.log("entro aqui? program jjsonContent", prCopy.jsonContent);
                prCopy.jsonContent = await convertAllBlobsFromJson(
                    prCopy.jsonContent,
                    prCopy.id
                );
            }

            const result = await update(prCopy);
            if (result.ok) {
                setProgram(result.item);
            }

            //  Mostrar mensaje de éxito
            addSnackbar(MessageSnackbarDefault.successEditDefault);

        } catch (error) {
            console.error("Error al actualizar Program", error);
            addSnackbar(MessageSnackbarDefault.errorDefault);
        } finally {
            setLoading(false);
        }
    }, [update, addSnackbar]);

    /**
     * Add
     */
    const addProgram = useCallback(async (
        program: Program,
        imageItem: {
            oldImage: string;
            image: string;
            nameImage: string;
        }
    ) => {
        try {
            setLoading(true);
            console.log("creando Program con ID:", id);
            console.log("creando Program con ID:", id);
            console.log("creando Program con ID:", id);
            // 1. Crear una copia del programa sin la imagen principal y el jsonContent
            const programToCreate = { ...program };
            delete programToCreate.image;
            delete programToCreate.jsonContent;

            // 2. Crear el Program sin imágenes
            const createResult = await add(programToCreate);
            if (!createResult.ok) {
                throw new Error("Error al crear el Program");
            }

            const newProgram = createResult.item;
            setTypeAction("update");
            setProgram(newProgram);

            const newProgramId = newProgram.id;

            // 3. Subir la imagen principal si existe y es un blob
            let mainImagePath = newProgram.image;
            if (imageItem.image && imageItem.image.startsWith("blob:")) {

                // let fileName = `program_main_${Date.now()}.png`;
                // const file = await Util.transformBlobToFile(imageItem.image, fileName);
                // mainImagePath = await createFile(file, newProgramId);
                const lastDotIndex = imageItem.nameImage.lastIndexOf(".");
                // const name = lastDotIndex > -1 ? imageItem.nameImage.slice(0, lastDotIndex) : imageItem.nameImage;
                const name = Date.now() + "_" + Math.random().toString(36).substring(7);
                const ext = lastDotIndex > -1 ? imageItem.nameImage.slice(lastDotIndex) : ".png";
                const formattedName = name.replace(/ /g, "_");
                let file = await Util.transformBlobToFile(
                    imageItem.image,
                    formattedName + "_" + Date.now() + ext
                );
                mainImagePath = await createFile(file, newProgramId);
            }

            // 4. Subir las imágenes secundarias dentro de jsonContent
            let updatedJsonContent = program.jsonContent;
            if (updatedJsonContent) {
                updatedJsonContent = await convertAllBlobsFromJson(updatedJsonContent, newProgramId);
            }

            if (!newProgram.startPublicationDate) {
                newProgram.startPublicationDate = moment().subtract(5, "minutes").toDate();
            }

            // 5. Actualizar el Program con las rutas de las imágenes
            const updatedProgram = {
                ...newProgram,
                image: mainImagePath,
                jsonContent: updatedJsonContent
            };

            const updateResult = await update(updatedProgram);
            if (updateResult.ok) {
                setProgram(updateResult.item);
            } else {
                throw new Error("Error al actualizar el Program con las imágenes");
            }

            // 6. Mostrar mensaje de éxito
            addSnackbar(MessageSnackbarDefault.successAddDefault);

        } catch (error) {
            console.error("Error al agregar Program", error);
            addSnackbar(MessageSnackbarDefault.errorDefault);
        } finally {
            setLoading(false);
        }
    }, [add, update, addSnackbar]);

    /**
     * Check SEO param (si lo necesitas)
     */
    const checkProgramSlug = async (id: string, value: string) => {
        try {
            const result = await checkSpecificParamSEO(id, value, "slug");
            return result;
        } catch (error) {
            console.error("Error al verificar el slug del Program", error);
            return false;
        }
    };

    /**
     * Manejo final en el formulario
     */
    const handleRecord = (
        value: Program,
        imageItem: {
            oldImage: string;
            image: string;
            nameImage: string;
        }
    ) => {
        if (typeAction === "add" && !value?.id) {
            addProgram(value, imageItem);
        } else if (typeAction === "update" && value?.id !== undefined) {
            updateProgram(value, imageItem);
        } else {
            console.warn("Error, no hay acción definida");
        }
    };

    return (
        <CContainer style={{ display: "flex", flexDirection: "column" }}>
            <div className="w-full justify-between items-center flex pt-4 pb-5">
                <div className="flex flex-col">
                    <div
                        className="flex items-center hover:underline cursor-pointer mb-5"
                        onClick={() => {
                            const pagesRoutes = t("routes", { returnObjects: true });
                            let newRoute = ROUTE_NAME_ID_2({}, pagesRoutes, RouteName.program);
                            navigate(`${BASE_ROUTE}${newRoute}`);
                        }}
                    >
                        <HiArrowNarrowLeft size={20} className="mr-1" />
                        {t("screens.program.labelGoBack") || "Volver a Programas"}
                    </div>

                    <span className="text-4xl" style={{ fontWeight: 500 }}>
                        {t("screens.program.title") || "Programs"}
                    </span>
                    <span className="small text-medium-emphasis">
                        {typeAction === "add"
                            ? t("screens.program.labelCreate") || "Crear Program"
                            : t("screens.program.labelDetail") || "Detalle Program"}
                    </span>
                </div>
            </div>

            <div className="w-full flex justify-center mb-10">
                <div className="w-full flex justify-center gap-4 flex-col xl:flex-row ">
                    <div
                        className={clsx(`flex flex-col w-full justify-center items-center`, {
                            "xl:w-[100%]": program?.id,
                        })}
                    >
                        <div className="bg-white block p-4 md:p-7 shadow-md md:rounded-lg w-full md:w-[70%]">
                            <div className="text-2xl font-medium mb-4">
                                {t("screens.program.subtitle_1") || "Información del Programa"}
                            </div>
                            <div className="w-full flex flex-col md:flex-row justify-center md:gap-4">
                                {loading && (
                                    <div
                                        className="w-full flex flex-col justify-center items-center"
                                        style={{ height: 547 }}
                                    >
                                        <GridLoader color="#3d4f65" size={25} speedMultiplier={0.7} />
                                    </div>
                                )}

                                {!loading && program && (
                                    <div ref={formRef} className="p-0">
                                        <ProgramForm
                                            initialProgram={program}
                                            checkProgramSlug={checkProgramSlug}
                                            onSubmit={(program, imageItem) => {
                                                handleRecord(program, imageItem);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CContainer>
    );
};

export default ProgramDetail;
